import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';
import ProjectHistorySection from '../components/ProjectHistorySection';
import type {
  ProjectListResponse,
  ProjectSummary,
  Scan,
  ScanStatus,
} from '../types';

export default function HistoryPage() {
  const { t } = useTranslation();
  const { scans, isLoadingScans, scansError, fetchScans, setCurrentScan } =
    useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setIsLoadingProjects(true);
    setProjectsError(null);
    try {
      const res = await api.get<ProjectListResponse>('/api/projects');
      setProjects(res.data.projects);
    } catch (error) {
      console.error(error);
      setProjectsError(t('history.projects.loadError'));
    } finally {
      setIsLoadingProjects(false);
    }
  }, [t]);

  useEffect(() => {
    fetchScans();
    fetchProjects();
  }, [fetchProjects, fetchScans]);

  const handleView = (scanId: string) => {
    const scan = scans.find(item => item.id === scanId);
    if (scan) setCurrentScan(scan);
    navigate(`/scan/${scanId}/results`);
  };

  const handleOpenProject = (project: ProjectSummary) => {
    navigate(`/projects/${project.id}`);
  };

  return (
    <div className="animate-fade-in">
      <div className="page-intro">
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 32,
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: '-0.02em',
            marginBottom: 8,
          }}
        >
          {t('history.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {t('history.projectCount', { count: projects.length })}
        </p>
      </div>

      <ProjectHistorySection
        projects={projects}
        isLoading={isLoadingProjects}
        error={projectsError}
        onRetry={fetchProjects}
        onOpen={handleOpenProject}
      />

      {isLoadingScans ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div
            style={{
              width: 36,
              height: 36,
              border: '3px solid var(--border)',
              borderTopColor: 'var(--accent-primary)',
              borderRadius: '50%',
            }}
            className="animate-spin"
          />
        </div>
      ) : scansError ? (
        <div
          style={{
            padding: '24px',
            background: 'var(--danger-dim)',
            border: '1px solid rgba(255,23,68,0.2)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            {t('errors.generic')}
          </div>
          <div style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>
            {scansError}
          </div>
          <button
            className="button button-secondary"
            onClick={() => fetchScans()}
          >
            {t('history.retry')}
          </button>
        </div>
      ) : scans.length === 0 ? (
        isLoadingProjects || projectsError ? null : projects.length === 0 ? (
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: '48px 24px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 56, marginBottom: 16 }}>📋</div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 22,
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: 8,
              }}
            >
              {t('history.empty')}
            </h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: 28 }}>
              {t('history.emptyHint')}
            </p>
            <button
              onClick={() => navigate('/scan')}
              className="button button-primary"
            >
              {t('history.startFirstScan')}
            </button>
          </div>
        ) : null
      ) : (
        <details style={{ marginBottom: 32 }}>
          <summary
            style={{
              cursor: 'pointer',
              marginBottom: 14,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 12,
              }}
            >
              <span
                style={{
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-display)',
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {t('history.scanHistoryTitle')}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                {t('history.completedCount', { count: scans.length })}
              </span>
            </span>
          </summary>
          <section
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
            }}
          >
            <div className="desktop-only">
              <div className="history-table-head">
                <span>{t('history.columns.document')}</span>
                <span>{t('history.columns.actionSummary')}</span>
                <span>{t('history.columns.risks')}</span>
                <span>{t('history.columns.date')}</span>
                <span>{t('history.columns.status')}</span>
                <span />
              </div>

              {scans.map((scan, index) => (
                <div
                  key={scan.id}
                  className="history-table-row"
                  style={{
                    borderBottom:
                      index < scans.length - 1
                        ? '1px solid var(--border)'
                        : 'none',
                    animation: `fadeIn 0.3s ease ${index * 0.04}s backwards`,
                  }}
                >
                  <DocumentSummary
                    filename={scan.filename}
                    fileSize={scan.fileSize}
                  />

                  <div>
                    {scan.status === 'done' ? (
                      <GapSummary scan={scan} />
                    ) : (
                      <span
                        style={{ color: 'var(--text-muted)', fontSize: 13 }}
                      >
                        -
                      </span>
                    )}
                  </div>

                  <div>
                    {scan.status === 'done' ? (
                      <RiskBadge risks={scan.risks} />
                    ) : (
                      <span
                        style={{ color: 'var(--text-muted)', fontSize: 13 }}
                      >
                        -
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {format(new Date(scan.createdAt), 'MMM d, yyyy HH:mm')}
                  </div>

                  <div>
                    <StatusBadge status={scan.status} t={t} />
                  </div>

                  <div>
                    <HistoryActionButton
                      status={scan.status}
                      onView={() => handleView(scan.id)}
                      onProgress={() => navigate(`/scan/${scan.id}/processing`)}
                      labelDone={t('history.viewResults')}
                      labelProcessing={t('history.viewProgress')}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mobile-only history-mobile-list">
              {scans.map(scan => (
                <article key={scan.id} className="history-mobile-card">
                  <DocumentSummary
                    filename={scan.filename}
                    fileSize={scan.fileSize}
                  />

                  <div className="history-mobile-meta">
                    <div className="label-stack">
                      <span className="label-stack-label">
                        {t('history.columns.actionSummary')}
                      </span>
                      <div>
                        {scan.status === 'done' ? (
                          <GapSummary scan={scan} />
                        ) : (
                          <span
                            style={{ color: 'var(--text-muted)', fontSize: 13 }}
                          >
                            -
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="label-stack">
                      <span className="label-stack-label">
                        {t('history.columns.risks')}
                      </span>
                      <div>
                        {scan.status === 'done' ? (
                          <RiskBadge risks={scan.risks} />
                        ) : (
                          <span
                            style={{ color: 'var(--text-muted)', fontSize: 13 }}
                          >
                            -
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="label-stack">
                      <span className="label-stack-label">
                        {t('history.columns.date')}
                      </span>
                      <span
                        style={{ fontSize: 13, color: 'var(--text-secondary)' }}
                      >
                        {format(new Date(scan.createdAt), 'MMM d, yyyy HH:mm')}
                      </span>
                    </div>

                    <div className="label-stack">
                      <span className="label-stack-label">
                        {t('history.columns.status')}
                      </span>
                      <div>
                        <StatusBadge status={scan.status} t={t} />
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <HistoryActionButton
                      status={scan.status}
                      onView={() => handleView(scan.id)}
                      onProgress={() => navigate(`/scan/${scan.id}/processing`)}
                      labelDone={t('history.viewResults')}
                      labelProcessing={t('history.viewProgress')}
                      fullWidth
                    />
                  </div>
                </article>
              ))}
            </div>
          </section>
        </details>
      )}
    </div>
  );
}

function DocumentSummary({
  filename,
  fileSize,
}: {
  filename: string;
  fileSize: number;
}) {
  return (
    <div className="document-summary">
      <span style={{ fontSize: 20, flexShrink: 0 }}>📄</span>
      <div className="document-summary-copy">
        <div
          className="document-summary-title"
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-primary)',
          }}
        >
          {filename}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {fileSize ? formatBytes(fileSize) : ''}
        </div>
      </div>
    </div>
  );
}

function HistoryActionButton({
  status,
  onView,
  onProgress,
  labelDone,
  labelProcessing,
  fullWidth = false,
}: {
  status: ScanStatus;
  onView: () => void;
  onProgress: () => void;
  labelDone: string;
  labelProcessing: string;
  fullWidth?: boolean;
}) {
  if (status === 'done' || status === 'error') {
    return (
      <button
        onClick={onView}
        className="button button-secondary"
        style={{
          width: fullWidth ? '100%' : undefined,
          minHeight: 'auto',
          padding: '10px 14px',
          borderColor: 'rgba(0, 200, 255, 0.2)',
          background: 'var(--accent-dim)',
          color: 'var(--accent-primary)',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {status === 'done' ? labelDone : 'View Details'}
      </button>
    );
  }

  if (status === 'processing') {
    return (
      <button
        onClick={onProgress}
        className="button button-warning"
        style={{
          width: fullWidth ? '100%' : undefined,
          minHeight: 'auto',
          padding: '10px 14px',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {labelProcessing}
      </button>
    );
  }

  return null;
}

function GapSummary({ scan }: { scan: Scan }) {
  const { t } = useTranslation();
  const findings = scan.findings ?? [];
  const highGaps = findings.length
    ? findings.filter(
        finding => finding.severity === 'high' && finding.type !== 'strength'
      ).length
    : scan.risks.filter(risk => risk.severity === 'high').length;
  const needsReview = findings.filter(
    finding => finding.reviewStatus === 'needs_review'
  ).length;
  return (
    <span
      style={{
        display: 'inline-block',
        color: highGaps > 0 ? 'var(--danger)' : 'var(--text-secondary)',
        fontSize: 12,
        lineHeight: 1.4,
        fontWeight: highGaps > 0 ? 700 : 400,
      }}
    >
      {t('history.actionSummary.highGaps', { count: highGaps })}
      {needsReview > 0 && (
        <>
          {' · '}
          {t('history.actionSummary.needsReview', { count: needsReview })}
        </>
      )}
    </span>
  );
}

function RiskBadge({ risks }: { risks: { severity: string }[] }) {
  const high = risks.filter(risk => risk.severity === 'high').length;
  const color = high > 0 ? 'var(--danger)' : 'var(--text-secondary)';

  return (
    <span style={{ fontSize: 13, color, fontWeight: high > 0 ? 700 : 400 }}>
      {risks.length}{' '}
      {high > 0 && (
        <span style={{ fontSize: 11, color: 'var(--danger)' }}>
          ({high} high)
        </span>
      )}
    </span>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: ScanStatus;
  t: (key: string) => string;
}) {
  const styles: Record<ScanStatus, { color: string; bg: string }> = {
    done: { color: 'var(--success)', bg: 'var(--success-dim)' },
    processing: { color: 'var(--warning)', bg: 'var(--warning-dim)' },
    error: { color: 'var(--danger)', bg: 'var(--danger-dim)' },
    pending: { color: 'var(--text-muted)', bg: 'var(--bg-tertiary)' },
  };

  const state = styles[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        padding: '3px 10px',
        background: state.bg,
        color: state.color,
        borderRadius: 100,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {t(`history.status.${status}`)}
    </span>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}
